'use client';

import { useTranslations } from '@power-core/toolkit';
import { type FunctionComponent, useEffect, useState } from 'react';
import AbbTabSelectorRed from '@/assets/icons/abb_tab-selector_red.svg';
import { AbbLeftArrow } from '@/assets/icons/components/abb-left-arrow';
import { AbbRightArrow } from '@/assets/icons/components/abb-right-arrow';
import { useAppContext } from '@/modules/app-context/use-app-context';
import { useCheckTranslatedString } from '@/modules/utils/custom-hooks/use-check-translated-string';
import { useDevice } from '@/modules/utils/custom-hooks/use-device';
import { useTheme } from '@/modules/utils/custom-hooks/use-theme';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { IconButton } from '@/ui-components/icon-button/icon-button';
import { Paragraph } from '@/ui-components/paragraph/paragraph';
import { ActionButtons } from './components/action-buttons';
import { CardIcons } from './components/card-icons';
import { useSmartCardsInit } from './hooks/use-smart-cards-init';
import { useSmartCardsParams } from './hooks/use-smart-cards-params';

const CARDS_ON_ONE_SCREEN = 3;

export const SmartCards: FunctionComponent = () => {
	useSmartCardsInit();
	const checkTranslatedString = useCheckTranslatedString();
	const t = useTranslations();
	const { releaseNotesStatus, documentTypes } = useAppContext();
	const { isPending, isDisabled } = useSmartCardsParams();
	const { isMobile, isLaptop } = useDevice();
	const [showAllCards, setShowAllCards] = useState(false);
	const [showMoreCardsButton, setShowMoreCardsButton] = useState(false);
	const { isDarkTheme } = useTheme();
	const cards = showAllCards ? documentTypes : (documentTypes?.slice(0, CARDS_ON_ONE_SCREEN) ?? []);

	useEffect(() => {
		if (isPending || !documentTypes || documentTypes.length <= CARDS_ON_ONE_SCREEN) return;

		setShowMoreCardsButton(true);
	}, [isPending, documentTypes?.length]);

	return (
		<div
			className={`col-span-12 grid ${showMoreCardsButton && !showAllCards ? 'grid-cols-11 2xl:grid-cols-10' : 'grid-cols-12'} gap-1 x-large:gap-4`}
		>
			{cards?.map((card, index) => (
				<div
					key={card.title}
					className={`${showMoreCardsButton && !showAllCards ? 'col-span-3' : 'col-span-4'} ${index === 0 ? ' step-2' : ''}`}
				>
					<Flexbox direction='col' className='w-full h-full p-2 xl:p-4 bg-white rounded-card dark:bg-card_dark '>
						<Flexbox justify='between' className='relative'>
							<Flexbox items='center' justify='center' className='gap-2'>
								<Paragraph variant={isLaptop ? 'text-base-semibold' : 'h5'}>{checkTranslatedString(card.title)}</Paragraph>
								{card.isBeta && (
									<Paragraph variant='text-xs' className='bg-[#3366ff40] px-2 py-0.5 rounded-card !text-accent'>
										{t('T_BETA')}
									</Paragraph>
								)}
							</Flexbox>
							{<CardIcons documentTypeId={card.documentId} isViewed={releaseNotesStatus[card.documentId] ?? true} />}
						</Flexbox>
						<Paragraph variant={'text-sm-thin'} className='grow py-1 small:py-0'>
							{checkTranslatedString(card.subtitle)}
						</Paragraph>
						{!isMobile && (
							<img
								key={card.documentId}
								src={isDarkTheme ? card?.imageDark?.url : card?.image?.url}
								alt={checkTranslatedString(card.title)}
								className='pt-2 w-full'
							/>
						)}
						<ActionButtons index={index} isDisabled={isDisabled} isLoading={isPending} card={card} />
					</Flexbox>
				</div>
			))}
			{showMoreCardsButton && (
				<div
					onClick={() => setShowAllCards(!showAllCards)}
					className={`relative ${showAllCards ? 'col-span-2' : '2xl:col-span-1 col-span-2'} rounded-xl bg-black overflow-hidden p-4 cursor-pointer
          
          before:bg-repeat-no-repeat
          before:content-['']
          before:absolute
          before:bottom-0
          ${showAllCards ? 'before:-left-0 before:w-1/2 before:h-1/2' : 'before:-left-1/2 before:w-full before:h-1/2'}
          before:bg-[radial-gradient(circle,rgba(231,132,16,0.35)_0%,rgba(231,132,16,0.18)_35%,transparent_70%)]

          after:bg-repeat-no-repeat
          after:content-['']
          after:absolute
          after:top-0
          ${showAllCards ? 'after:-right-0 after:w-1/2 after:h-1/2' : 'after:-right-1/2 after:w-full after:h-1/2'}
          after:bg-[radial-gradient(circle,rgba(231,132,16,0.35)_0%,rgba(231,132,16,0.18)_35%,transparent_70%)]
      `}
				>
					<Flexbox direction='col' justify='between' className='h-full'>
						<div>
							<AbbTabSelectorRed />
							<Paragraph variant='h5' className='leading-none pt-2 text-white'>
								{showAllCards ? t('T_SHOW_LESS_TOOLS') : t('T_SHOW_MORE_TOOLS')}
							</Paragraph>
						</div>

						<IconButton
							icon={showAllCards ? <AbbLeftArrow fill='white' size={24} /> : <AbbRightArrow fill='white' size={24} />}
							variant='primary'
						/>
					</Flexbox>
				</div>
			)}
		</div>
	);
};
